import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fr, type TranslationDictionary, type TranslationKey } from "./translations/types";
import {
  OUVERTURE_DEFAUT,
  aujourdhuiParis,
  insererPhrases,
  phrasesOuverture,
  type OuverturePage,
} from "@shared/ouverture";

export type Language = "fr" | "en" | "de" | "nl";
export type { TranslationKey };

const LANGUAGE_STORAGE_KEY = "villa_lang";
const LANGUAGE_LOADERS: Record<Language, () => Promise<{ default: TranslationDictionary }>> = {
  fr: async () => ({ default: fr }),
  en: () => import("./translations/en"),
  de: () => import("./translations/de"),
  nl: () => import("./translations/nl"),
};

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: TranslationKey) => string;
  isLanguageLoading: boolean;
  /** Saison et horaires (Paramètres du back-office) et phrases prêtes à afficher dans la langue courante. */
  ouverture: OuverturePage;
  phrases: Record<string, string>;
}

/**
 * Saison et horaires : écrits dans chaque page au pré-rendu, avec les réglages en base
 * (<script type="application/json" id="ouverture-data">, voir scripts/prerender.mjs) ;
 * pendant le pré-rendu : globalThis.__OUVERTURE__ ; à défaut : valeurs par défaut et date du jour.
 */
let ouvertureLue: OuverturePage | null = null;
export function ouvertureInitiale(): OuverturePage {
  const valide = (o: OuverturePage | undefined | null): o is OuverturePage =>
    !!o?.reglages?.horaires && /^\d{4}-\d{2}-\d{2}$/.test(o.jour ?? "");
  const prerendu = (globalThis as { __OUVERTURE__?: OuverturePage }).__OUVERTURE__;
  if (valide(prerendu)) return prerendu;
  if (ouvertureLue) return ouvertureLue;
  try {
    const bloc = typeof document !== "undefined" ? document.getElementById?.("ouverture-data")?.textContent : null;
    const lue = bloc ? (JSON.parse(bloc) as OuverturePage) : null;
    ouvertureLue = valide(lue) ? lue : { reglages: OUVERTURE_DEFAUT, jour: aujourdhuiParis() };
  } catch {
    ouvertureLue = { reglages: OUVERTURE_DEFAUT, jour: aujourdhuiParis() };
  }
  return ouvertureLue;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

function isLanguage(value: string | null): value is Language {
  return value === "fr" || value === "en" || value === "de" || value === "nl";
}

function getStoredLanguage(): Language {
  // Les balises hreflang annoncent les versions linguistiques en « ?lang=en » (etc.) :
  // ce paramètre doit donc afficher la langue annoncée.
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  if (isLanguage(fromUrl)) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, fromUrl);
    return fromUrl;
  }
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return stored === "en" || stored === "de" || stored === "nl" ? stored : "fr";
}

// Dictionnaires déjà chargés (le français est toujours inclus).
const dictionnairesCharges: Partial<Record<Language, TranslationDictionary>> = { fr };

/** Charge une langue avant le rendu (pages /en, /de, /nl pré-rendues et leur reprise par React). */
export async function prechargerLangue(lang: Language) {
  if (!dictionnairesCharges[lang]) dictionnairesCharges[lang] = (await LANGUAGE_LOADERS[lang]()).default;
}

// Reprise d'une page pré-rendue (en français) : le premier rendu doit être identique au HTML reçu ;
// la langue choisie par le visiteur est appliquée juste après.
let repriseEnCours =
  typeof document !== "undefined" && document.getElementById?.("root")?.dataset?.prerendered === "1";

export function LanguageProvider({ children, initialLang }: { children: React.ReactNode; initialLang?: Language }) {
  const [initial] = useState(() => {
    const l: Language = initialLang ?? (repriseEnCours ? "fr" : getStoredLanguage());
    return { lang: l, dictionnaire: dictionnairesCharges[l] };
  });
  const [lang, setLangState] = useState<Language>(initial.lang);
  const [dictionary, setDictionary] = useState<TranslationDictionary>(initial.dictionnaire ?? fr);
  const [isLanguageLoading, setIsLanguageLoading] = useState(initial.lang !== "fr" && !initial.dictionnaire);
  // Mêmes réglages et même jour de référence que le HTML reçu (pages refaites chaque nuit par le
  // serveur) : la reprise par React, y compris des parties chargées plus tard, affiche les mêmes textes.
  const [ouverture] = useState<OuverturePage>(ouvertureInitiale);
  const phrases = useMemo(() => phrasesOuverture(ouverture.reglages, lang, ouverture.jour), [ouverture, lang]);

  useEffect(() => {
    if (!repriseEnCours) return;
    repriseEnCours = false;
    if (!initialLang) {
      const choisie = getStoredLanguage();
      if (choisie !== "fr") setLangState(choisie);
    }
  }, [initialLang]);

  useEffect(() => {
    if (lang === "fr") {
      setDictionary(fr);
      setIsLanguageLoading(false);
      return;
    }

    const deja = dictionnairesCharges[lang];
    if (deja) {
      setDictionary(deja);
      setIsLanguageLoading(false);
      return;
    }

    let current = true;
    setIsLanguageLoading(true);

    LANGUAGE_LOADERS[lang]().then(({ default: loadedDictionary }) => {
      dictionnairesCharges[lang] = loadedDictionary;
      if (current) {
        setDictionary(loadedDictionary);
        setIsLanguageLoading(false);
      }
    });

    return () => {
      current = false;
    };
  }, [lang]);

  const setLang = useCallback((newLang: Language) => {
    setLangState(newLang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
  }, []);

  const t = useCallback((key: TranslationKey): string => {
    return insererPhrases(dictionary[key] || fr[key] || key, phrases);
  }, [dictionary, phrases]);

  const value = useMemo(
    () => ({ lang, setLang, t, isLanguageLoading, ouverture, phrases }),
    [lang, setLang, t, isLanguageLoading, ouverture, phrases],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
      {isLanguageLoading && (
        <div
          role="status"
          aria-label={t("language_loading")}
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 10000,
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "2px solid rgba(184, 151, 90, 0.28)",
            borderTopColor: "#b8975a",
            background: "rgba(14, 12, 10, 0.9)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            animation: "language-loader-spin 0.7s linear infinite",
          }}
        />
      )}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}

/** Saison et horaires avec leurs phrases (langue courante, ou celle demandée), utilisable hors du LanguageProvider. */
export function useOuverture(langue?: Language) {
  const ctx = useContext(LanguageContext);
  const ouverture = ctx?.ouverture ?? ouvertureInitiale();
  const l = langue ?? ctx?.lang ?? "fr";
  const phrases = useMemo(
    () => (ctx && l === ctx.lang ? ctx.phrases : phrasesOuverture(ouverture.reglages, l, ouverture.jour)),
    [ctx, l, ouverture.reglages, ouverture.jour],
  );
  return { reglages: ouverture.reglages, jour: ouverture.jour, phrases };
}
